import React from 'react';
import { RealtimeWaveState } from './OnlineVoiceRecorderModels';
import { RecordingAnimation } from './RecordingAnimation';
/**
 * Modern component that visualizes audio being recorded using Web Audio API AnalyserNode
 * Replaces the deprecated ScriptProcessorNode-based wavesurfer.js implementation
 *
 * Now accepts a mediaStream prop to avoid requesting duplicate microphone access,
 * which is required for iOS Safari compatibility
 */
export class ModernRecordingWaveform extends React.Component {
    constructor(props) {
        super(props);
        this.draw = () => {
            if (!this.analyser || !this.dataArray || !this.canvasRef.current) {
                return;
            }
            const canvas = this.canvasRef.current;
            const canvasContext = canvas.getContext('2d');
            if (!canvasContext) {
                return;
            }
            // Continue animation loop
            this.animationFrameId = requestAnimationFrame(this.draw);
            // Get frequency data
            this.analyser.getByteTimeDomainData(this.dataArray);
            // Clear canvas with transparency
            canvasContext.clearRect(0, 0, canvas.width, canvas.height);
            // Draw waveform
            canvasContext.lineWidth = 3.816;
            canvasContext.strokeStyle = '#5B24FF'; // Violet-500 from Figma
            canvasContext.beginPath();
            const sliceWidth = (canvas.width * 1.0) / this.dataArray.length;
            let x = 0;
            for (let i = 0; i < this.dataArray.length; i++) {
                const v = this.dataArray[i] / 128.0;
                const y = (v * canvas.height) / 2;
                if (i === 0) {
                    canvasContext.moveTo(x, y);
                }
                else {
                    canvasContext.lineTo(x, y);
                }
                x += sliceWidth;
            }
            canvasContext.lineTo(canvas.width, canvas.height / 2);
            canvasContext.stroke();
        };
        this.onErrorCallback = () => {
            console.error('[ModernRecordingWaveform] Error callback triggered');
            this.cleanup();
            this.setState({
                waveState: RealtimeWaveState.Unsupported,
            });
        };
        this.renderRealtimeWaveform = () => {
            return (React.createElement("canvas", { ref: this.canvasRef, id: "realtime-wave-canvas", width: 800, height: 140, style: { width: '100%', height: '140px' } }));
        };
        this.renderUnsupportedState = () => {
            return (React.createElement("div", null,
                React.createElement("div", { id: "pulse-spacing-adjustment", className: "h5" }),
                React.createElement(RecordingAnimation, null)));
        };
        this.state = {
            waveState: RealtimeWaveState.Loading,
        };
        this.canvasRef = React.createRef();
    }
    async componentDidMount() {
        // Wait for mediaStream to be provided from parent
        if (this.props.mediaStream) {
            try {
                await this.initializeAudioVisualization();
            }
            catch (error) {
                console.error('[ModernRecordingWaveform] Failed to initialize audio visualization:', error);
                console.error('[ModernRecordingWaveform] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
                this.onErrorCallback();
            }
        }
    }
    async componentDidUpdate(prevProps) {
        // Initialize visualization when mediaStream becomes available
        if (!prevProps.mediaStream && this.props.mediaStream && this.state.waveState === RealtimeWaveState.Loading) {
            try {
                await this.initializeAudioVisualization();
            }
            catch (error) {
                console.error('[ModernRecordingWaveform] Failed to initialize audio visualization:', error);
                console.error('[ModernRecordingWaveform] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
                this.onErrorCallback();
            }
        }
    }
    componentWillUnmount() {
        this.cleanup();
    }
    async initializeAudioVisualization() {
        try {
            if (!this.props.mediaStream) {
                throw new Error('No mediaStream provided');
            }
            // Create audio context and analyser
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            this.audioContext = new AudioContextClass();
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 2048;
            this.analyser.smoothingTimeConstant = 0.8;
            // Use the provided mediaStream instead of requesting a new one
            const source = this.audioContext.createMediaStreamSource(this.props.mediaStream);
            source.connect(this.analyser);
            // Prepare data array for frequency data
            const bufferLength = this.analyser.frequencyBinCount;
            this.dataArray = new Uint8Array(bufferLength);
            this.setState({
                waveState: RealtimeWaveState.Supported,
            }, () => {
                // Start visualization
                this.draw();
            });
        }
        catch (error) {
            console.error('[ModernRecordingWaveform] Error in initializeAudioVisualization:', error);
            this.onErrorCallback();
            throw error;
        }
    }
    cleanup() {
        // Cancel animation frame
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = undefined;
        }
        // Close audio context
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
            this.audioContext = undefined;
        }
        // NOTE: We do NOT stop the mediaStream here because we don't own it
        // The parent component (OnlineVoiceRecorder via MediaRecorder) owns and manages the stream
    }
    render() {
        return this.state.waveState === RealtimeWaveState.Unsupported
            ? this.renderUnsupportedState()
            : this.renderRealtimeWaveform();
    }
}
